/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box, Checkbox, TextField, Tooltip } from '@atgfr8/atg-ui-tailwind';
import {
  equipmentTypes,
  intermodalEquipmentTypes,
  loadModesTypes,
} from '@common/commonLists';
import { reeferEquipmentTypes } from '@common/order/constants';
import { SelectInput } from '@components';
import {
  isReefer,
  orderStore,
  setLoadSureStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { Show } from 'solid-js';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';

import { isLoadSureVendorAttached, loadId } from './LoadSureHelpers';
import { ReeferSettings } from './ReeferSettings';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const LoadDetailsRow = (props: PropsT) => {
  return (
    <div class="mb-2 pr-1">
      <div class="text-base text-armstrong-primary font-semibold">
        Load Details
      </div>
      <div class="flex gap-x-2 my-2 w-full min-w-0">
        <div class="gap-y-2 flex flex-col flex-1 min-w-0">
          <SelectInput
            label="Mode"
            menuItems={loadModesTypes.map((mode) => ({
              label: mode,
              value: mode,
            }))}
            onChange={(value) => {
              updateLoadPropertyAtIndex({
                // @ts-expect-error This is the same error in the old code.
                mode: value,
                teamRequired: value === 'LTL' ? false : props.load.teamRequired,
                secondDriver:
                  value === 'LTL' ? undefined : props.load.secondDriver,
                secondDriverPhoneNumber:
                  value === 'LTL'
                    ? undefined
                    : props.load.secondDriverPhoneNumber,
              });
            }}
            value={props.load.mode ?? ''}
            dataTestId={formatDataTestId('Mode', 'selectInput')}
          />
          <SelectInput
            label="Equipment"
            menuItems={(props.load.mode === 'Intermodal'
              ? intermodalEquipmentTypes
              : equipmentTypes
            ).map((item) => ({
              label: typeof item === 'string' ? item : item.label,
              value: typeof item === 'string' ? item : String(item.value),
            }))}
            onChange={(value) => {
              updateLoadPropertyAtIndex({
                equipment: value,
              });
              if (isLoadSureVendorAttached()) {
                setLoadSureStore('isDirty', (prev) => [...prev, loadId()]);
              }
            }}
            value={props.load.equipment ?? ''}
            dataTestId={formatDataTestId('Equipment', 'selectInput')}
          />
          <Show
            when={
              !!props.load.equipment &&
              reeferEquipmentTypes.includes(props.load.equipment)
            }
            fallback={
              <TextField
                label="Temperature Instructions"
                dataTestId={'temperature-instructions-textInput'}
                value={props.load.tempInstructions ?? ''}
                onChange={(val: string) => {
                  updateLoadPropertyAtIndex({
                    tempInstructions: val,
                  });
                }}
                size="sm"
                error={
                  orderStore.orderFormError &&
                  orderStore.orderFormError[
                    `loads[${props.tabIndex}].tempInstructions`
                  ]
                }
              />
            }
          >
            <Box>
              <ReeferSettings load={props.load} tabIndex={props.tabIndex} />
            </Box>
          </Show>
        </div>
        <div class="gap-y-2 flex flex-col flex-1 min-w-0">
          <TextField
            label="Product"
            value={props.load.product ?? ''}
            onChange={(val: string) => {
              updateLoadPropertyAtIndex({
                product: val,
              });
            }}
            size="sm"
            maxLength={30}
            dataTestId={'product-textInput'}
          />
          <TextField
            label="Tracking Number"
            value={props.load.trackingNumber ?? ''}
            onChange={(val: string) =>
              updateLoadPropertyAtIndex({
                trackingNumber: val,
              })
            }
            maxLength={60}
            size="sm"
            dataTestId={'tracking-number-textInput'}
          />
          <div class={`flex ${isReefer() ? 'flex-col' : ''} gap-2 flex-wrap `}>
            {props.load.mode !== 'Intermodal' ? (
              <>
                <Checkbox
                  label="Team"
                  checked={props.load.teamRequired}
                  onChange={(checked) => {
                    updateLoadPropertyAtIndex({
                      teamRequired: checked,
                      secondDriver: checked
                        ? props.load.secondDriver
                        : undefined,
                      secondDriverPhoneNumber: checked
                        ? props.load.secondDriverPhoneNumber
                        : undefined,
                    });
                  }}
                  dataTestId={'team-checkboxInput'}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
                <Checkbox
                  label="Over Dimension"
                  checked={props.load.od}
                  onChange={(checked) => {
                    updateLoadPropertyAtIndex({
                      od: checked,
                    });
                  }}
                  dataTestId={'over-dimension-checkboxInput'}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
                <Checkbox
                  label="Tarps Required"
                  checked={props.load.tarpRequired}
                  onChange={(checked) => {
                    updateLoadPropertyAtIndex({
                      tarpRequired: checked,
                    });
                  }}
                  dataTestId={'tarps-required-checkboxInput'}
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                />
              </>
            ) : null}
            <Checkbox
              label="Hazmat"
              checked={props.load.hazmatRequired}
              onChange={(checked) => {
                updateLoadPropertyAtIndex({
                  hazmatRequired: checked,
                });
              }}
              dataTestId={'hazmat-checkboxInput'}
              size="xs"
              color="info"
              class="ml-[-9px]"
            />
            <Checkbox
              label="Ramps Required"
              checked={props.load.ramps}
              onChange={(checked) => {
                updateLoadPropertyAtIndex({
                  ramps: checked,
                });
              }}
              dataTestId={'ramps-required-checkboxInput'}
              size="xs"
              color="info"
              class="ml-[-9px]"
            />
            <Show
              when={isFeatureFlagEnabled(
                FeatureFlags.feature_show_drop_trailer_accessorial,
              )()}
            >
              <Tooltip
                title={
                  props.load.dropTrailer
                    ? 'Deselect Drop Trailer on all stops to remove.'
                    : 'Select Drop Trailer on any stop to apply.'
                }
                placement="bottom-start"
                class="z-10"
              >
                <Checkbox
                  size="xs"
                  color="info"
                  class="ml-[-9px]"
                  label="Drop Trailer"
                  checked={props.load.dropTrailer}
                  disabled={true}
                  dataTestId={'drop-load-checkbox'}
                />
              </Tooltip>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
};
